import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import { Menu } from 'lucide-react';

interface TombstoneLayoutProps {
  children: ReactNode;
}

export function TombstoneLayout({ children }: TombstoneLayoutProps) {
  const location = useLocation();
  const { user } = useCurrentUser();

  const allNavItems = [
    { href: '/', label: 'Dashboard', authRequired: true },
    { href: '/relays', label: 'Relays', authRequired: true },
    { href: '/master', label: 'Master', authRequired: true },
    { href: '/docs', label: 'Docs', authRequired: false },
  ];

  const navItems = allNavItems.filter((item) => !item.authRequired || !!user);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top nav */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-14 grid grid-cols-3 items-center">
          {/* Left: Hamburger menu — all screen sizes */}
          <div className="flex items-center">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 -ml-1">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 bg-background border-border">
                <div className="flex flex-col gap-1 pt-8">
                  {navItems.map((item) => (
                    <SheetClose asChild key={item.href}>
                      <Link
                        to={item.href}
                        className={cn(
                          "px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-3",
                          location.pathname === item.href
                            ? "bg-secondary text-foreground"
                            : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                        )}
                      >
                        {item.label}
                      </Link>
                    </SheetClose>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Center: Full typography logo on all screens */}
          <div className="flex justify-center">
            <Link to="/" className="group hover:opacity-80 transition-opacity">
              <img 
                src="/tombstone_typography.png" 
                alt="Tombstone" 
                className="h-6 sm:h-8 object-contain" 
              />
            </Link>
          </div>

          {/* Right: Login */}
          <div className="flex justify-end">
            <LoginArea className="max-w-40 sm:max-w-56 justify-end" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 pt-14">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-border/40 py-6 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-xs text-muted-foreground">
          <span>Tombstone — Nostr Event Manager</span>
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground transition-colors"
          >
            Vibed with Shakespeare
          </a>
        </div>
      </footer>
    </div>
  );
}
